/**
 * 🥚🩸 DragoniousFightsEggNest — "Missing Out" Move-Discovery Hub
 *
 * Central Egg Nest 🪹 for the fighting-game mode. Players hatch the purple
 * & black "Dragonious Fighters Egg" (blood 🩸 droplet motif) once every
 * 12 hours to discover hidden moves for their heroes.
 *
 * Duplicates auto-convert to tiered currency:
 *   Normal Skill → Chits · Special → Seeds · Humiliation → ODs · Judgment → Bound OD
 */
import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Egg, Timer, Sparkles, Lock, Trophy, Coins, KeyRound, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import DragoniousCheatWall from "./DragoniousCheatWall";
import { GOLDEN_APEX_URL, DRAGONIOUS_MUTE_KEY } from "@/lib/dragoniousAudio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── UI helpers ─────────────────────────────────────────────
const TIER_META = {
  normal_skill:       { label: "Normal Skill",       accent: "text-neutral-300", border: "border-neutral-700",   bg: "bg-neutral-900/40",   chip: "bg-neutral-800 text-neutral-200" },
  special_move:       { label: "Special Move",       accent: "text-cyan-300",    border: "border-cyan-500/40",   bg: "bg-cyan-950/30",      chip: "bg-cyan-900/50 text-cyan-200" },
  humiliation:        { label: "Humiliation",        accent: "text-orange-300",  border: "border-orange-500/40", bg: "bg-orange-950/30",    chip: "bg-orange-900/50 text-orange-200" },
  showtime_fatality:  { label: "Showtime Fatality",  accent: "text-amber-300",   border: "border-amber-500/40",  bg: "bg-amber-950/30",     chip: "bg-amber-900/50 text-amber-200" },
  bloody_fatality:    { label: "Bloody Fatality",    accent: "text-red-300",     border: "border-red-500/40",    bg: "bg-red-950/30",       chip: "bg-red-900/50 text-red-200" },
  hero_villain_unlock:{ label: "Hero / Villain",     accent: "text-fuchsia-300", border: "border-fuchsia-500/40",bg: "bg-fuchsia-950/30",   chip: "bg-fuchsia-900/50 text-fuchsia-200" },
  stage_unlock:       { label: "Stage / Background", accent: "text-emerald-300", border: "border-emerald-500/40",bg: "bg-emerald-950/30",   chip: "bg-emerald-900/50 text-emerald-200" },
};

// Tiers that appear in the per-hero catalog UI (excludes roster-level drops).
const MOVE_TIERS_UI = ["normal_skill", "special_move", "humiliation", "showtime_fatality", "bloody_fatality"];

const CURRENCY_META = {
  chits:        { label: "Chits",             icon: "🪙", color: "text-yellow-300" },
  seeds:        { label: "Seeds",             icon: "🌱", color: "text-green-300" },
  bound_omni:   { label: "Bound OmniDollars", icon: "🔒", color: "text-blue-300" },
  unbound_omni: { label: "Unbound OmniDollars", icon: "💎", color: "text-fuchsia-300" },
};

function formatCountdown(seconds) {
  if (seconds <= 0) return "Ready!";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// ─── The Egg itself — purple/black with a blood droplet ────
function DragoniousEgg({ cracking, canHatch }) {
  return (
    <div className="relative flex justify-center items-center py-6" data-testid="dragonious-egg-visual">
      <div
        className={`relative transition-transform duration-500 ${cracking ? "animate-shake scale-110" : canHatch ? "hover:scale-105" : "opacity-70"}`}
        style={{ filter: cracking ? "drop-shadow(0 0 24px #dc2626)" : "drop-shadow(0 0 18px #6b21a8)" }}
      >
        {/* Egg shell */}
        <svg width="140" height="180" viewBox="0 0 140 180">
          <defs>
            <radialGradient id="eggGrad" cx="50%" cy="35%" r="70%">
              <stop offset="0%"  stopColor="#7e22ce" />
              <stop offset="55%" stopColor="#4c1d95" />
              <stop offset="100%" stopColor="#0a0a0a" />
            </radialGradient>
            <linearGradient id="eggShine" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.30" />
              <stop offset="50%"  stopColor="#ffffff" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <ellipse cx="70" cy="95" rx="58" ry="78" fill="url(#eggGrad)" stroke="#1a0d2b" strokeWidth="2" />
          <ellipse cx="70" cy="95" rx="58" ry="78" fill="url(#eggShine)" />
          {/* Crack lines when hatching */}
          {cracking && (
            <>
              <path d="M40 60 L55 75 L48 90 L62 105 L52 120" stroke="#facc15" strokeWidth="2.5" fill="none" />
              <path d="M92 55 L82 75 L96 90 L88 110" stroke="#facc15" strokeWidth="2.5" fill="none" />
            </>
          )}
        </svg>
        {/* Blood droplet sigil */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-6xl leading-none drop-shadow-lg" style={{ filter: "drop-shadow(0 2px 4px #7f1d1d)" }}>🩸</div>
          <div className="mt-1 text-[9px] uppercase tracking-widest text-red-300/80 font-bold">Dragonious</div>
          <div className="text-[9px] uppercase tracking-widest text-red-300/80 font-bold">Fights Egg</div>
        </div>
      </div>
    </div>
  );
}

// ─── Reveal Modal — shown after each hatch ─────────────────
function RevealModal({ result, onClose }) {
  // 🎵 Golden Apex — plays for the duration of the hatch reveal.
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(DRAGONIOUS_MUTE_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    if (!result) return;
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.volume = 0.55;
    el.muted = muted;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked — silent fallback */ });
    return () => {
      try { el.pause(); el.currentTime = 0; } catch { /* noop */ }
    };
  }, [result]);
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
    try { localStorage.setItem(DRAGONIOUS_MUTE_KEY, muted ? "1" : "0"); } catch { /* noop */ }
  }, [muted]);

  if (!result) return null;
  const tier = TIER_META[result.tier] || TIER_META.normal_skill;
  const isDup = result.duplicate;
  const cur = result.reward ? CURRENCY_META[result.reward.currency] : null;
  const unlockKind = result.unlock_kind || "move";

  // ── Big label per unlock kind ──────────────────────────
  const titleIcon = isDup
    ? "🔁"
    : unlockKind === "hero"  ? "🩸"
    : unlockKind === "stage" ? "🗺️"
    : "✨";
  const titleLabel = isDup
    ? "Duplicate — Converted"
    : unlockKind === "hero"  ? "New Fighter Unlocked!"
    : unlockKind === "stage" ? "New Arena Unlocked!"
    : `New ${tier.label} Hatched`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="egg-reveal-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative max-w-md w-full rounded-2xl border-2 ${tier.border} ${tier.bg} p-6 shadow-2xl`}
        style={{ boxShadow: "0 0 60px rgba(107, 33, 168, 0.6)" }}
      >
        {/* 🎵 Golden Apex soundtrack — loops during the hatch reveal. */}
        <audio ref={audioRef} src={GOLDEN_APEX_URL} loop preload="auto" data-testid="egg-reveal-audio" />

        {/* Mute toggle */}
        <button
          onClick={() => setMuted((m) => !m)}
          data-testid="egg-reveal-mute"
          className="absolute top-3 right-12 text-neutral-400 hover:text-fuchsia-300 p-1"
          title={muted ? "Unmute Golden Apex" : "Mute Golden Apex"}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>

        <button
          onClick={onClose}
          data-testid="egg-reveal-close"
          className="absolute top-3 right-3 text-neutral-400 hover:text-white text-2xl leading-none"
        >
          ×
        </button>

        <div className="text-center mb-4">
          <div className="text-5xl mb-2">{titleIcon}</div>
          <div className={`text-[10px] uppercase tracking-[0.3em] font-bold ${tier.accent}`}>
            {titleLabel}
          </div>
        </div>

        {/* ── Hero / Villain Unlock body ── */}
        {!isDup && unlockKind === "hero" && (
          <div className="rounded-xl border border-fuchsia-500/40 bg-black/40 p-4 mb-4" data-testid="egg-reveal-hero-unlock">
            <div className="flex items-center gap-3">
              <div className="text-5xl">{result.hero_icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">Fighter</div>
                <div className="text-xl font-bold text-white">{result.hero_name}</div>
                <div className={`inline-block mt-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded ${tier.chip}`}>
                  {tier.label}
                </div>
              </div>
            </div>
            <div className="text-xs text-fuchsia-200 leading-relaxed mt-3">{result.description}</div>
          </div>
        )}

        {/* ── Stage / Background Unlock body ── */}
        {!isDup && unlockKind === "stage" && (
          <div className="rounded-xl border border-emerald-500/40 bg-black/40 p-4 mb-4" data-testid="egg-reveal-stage-unlock">
            <div className="flex items-center gap-3">
              <div className="text-5xl">{result.stage_icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300">Arena</div>
                <div className="text-xl font-bold text-white">{result.stage_name}</div>
                {result.hero_tie_in && (
                  <div className="text-[10px] text-neutral-400 mt-0.5">Tied to · {result.hero_tie_in}</div>
                )}
                <div className={`inline-block mt-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded ${tier.chip}`}>
                  {tier.label}
                </div>
              </div>
            </div>
            <div className="text-xs text-emerald-200 leading-relaxed mt-3">{result.description}</div>
          </div>
        )}

        {/* ── Move reveal body (default) ── */}
        {unlockKind === "move" && (
          <div className="rounded-xl border border-neutral-800 bg-black/40 p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-3xl">{result.hero_icon}</span>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">{result.hero_name}</div>
                <div className={`text-lg font-bold text-white`}>{result.move_name}</div>
                <div className={`inline-block mt-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded ${tier.chip}`}>
                  {tier.label}
                </div>
              </div>
            </div>
            <div className="text-xs text-neutral-300 leading-relaxed mt-3">{result.description}</div>
            {result.input && (
              <div className={`mt-3 rounded-lg border-2 ${isDup ? "border-neutral-700 bg-black/50" : "border-emerald-500/60 bg-emerald-900/25"} p-2.5`} data-testid="egg-reveal-input">
                <div className={`text-[9px] uppercase tracking-[0.3em] font-bold mb-1.5 ${isDup ? "text-neutral-400" : "text-emerald-300"}`}>
                  {isDup ? "Command Input (already known)" : "Command Input · Now in your skill database"}
                </div>
                <div className={`text-xs font-mono font-bold ${isDup ? "text-neutral-300" : "text-emerald-200"} space-y-1`}>
                  <div data-testid="egg-reveal-input-pc"><span className="text-neutral-500 text-[10px]">PC:</span>&nbsp;&nbsp;▶ {result.input_pc || result.input}</div>
                  {result.input_mobile && (
                    <div data-testid="egg-reveal-input-mobile"><span className="text-neutral-500 text-[10px]">Mobile:</span>&nbsp;&nbsp;📱 {result.input_mobile}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {isDup && cur ? (
          <div className="rounded-xl border border-yellow-500/40 bg-yellow-900/20 p-4 text-center" data-testid="egg-reveal-duplicate">
            <div className="text-[9px] uppercase tracking-[0.3em] text-yellow-300 mb-1">Duplicate — Auto Refund</div>
            <div className="text-3xl">{cur.icon}</div>
            <div className={`text-xl font-bold ${cur.color}`}>+{result.reward.amount} {cur.label}</div>
          </div>
        ) : (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-900/20 p-3 text-center" data-testid="egg-reveal-new">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300">
              {unlockKind === "hero"  ? "Fighter Added · Now selectable on the roster" :
               unlockKind === "stage" ? "Arena Added · Now selectable on stage select" :
                                        "Move Unlocked · Now castable in the arcade"}
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-[10px] uppercase tracking-widest text-neutral-500">
          Next egg incubates for 12 hours
        </div>
      </div>
    </div>
  );
}

// ─── Hero mastery card ─────────────────────────────────────
function HeroMasteryCard({ hero, onOpen }) {
  const pct = hero.mastery_pct || 0;
  const notCanonized = !hero.canonized;
  const locked = hero.is_locked === true;   // owner-lock: hidden until first hatch
  const disabled = notCanonized || locked;
  return (
    <button
      disabled={disabled}
      onClick={() => onOpen(hero.hero_id)}
      data-testid={`egg-hero-card-${hero.hero_id}`}
      className={`text-left rounded-xl border-2 p-3 transition relative ${
        notCanonized
          ? "border-dashed border-neutral-800 bg-black/20 opacity-60 cursor-not-allowed"
          : locked
            ? "border-amber-700/40 bg-black/40 opacity-80 cursor-not-allowed"
            : "border-neutral-700 bg-neutral-900/40 hover:border-purple-500/50 hover:bg-purple-950/20"
      }`}
    >
      {locked && (
        <div className="absolute top-1.5 right-1.5 text-lg" title="Locked — hatch a move to unlock" data-testid={`egg-hero-locked-${hero.hero_id}`}>
          🔒
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className={`text-4xl ${locked ? "grayscale opacity-70" : ""}`}>{hero.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <div className="text-sm font-bold text-white truncate">
              {locked ? "???" : (hero.name || hero.hero_name)}
            </div>
            {hero.non_canon_playable && !locked && (
              <span
                data-testid={`egg-hero-noncanon-${hero.hero_id}`}
                className="text-[7px] uppercase tracking-widest px-1 py-0.5 rounded border border-neutral-600 bg-black/60 text-neutral-300 font-bold shrink-0"
                title="Non-canon but playable — Temporary Relief Hero"
              >
                Non-Canon
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            {notCanonized
              ? "Kit not canonized"
              : locked
                ? `Locked · 0 / ${hero.total} moves`
                : `${hero.known} / ${hero.total} known`}
          </div>
          {!disabled && (
            <div className="mt-1.5 h-1.5 rounded-full bg-neutral-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-500 to-red-500" style={{ width: `${pct}%` }} />
            </div>
          )}
          {locked && (
            <div className="mt-1 text-[9px] italic text-amber-400/80">Hatch any move to unlock</div>
          )}
        </div>
        {!disabled && <div className="text-[10px] font-bold text-purple-300">{pct}%</div>}
      </div>
    </button>
  );
}

// ─── Hero Detail Drawer — masked "Missing Out" list ────────
function HeroDetail({ heroId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!heroId) return;
    setLoading(true);
    axios
      .get(`${API}/dragonious-egg/catalog/${heroId}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load hero catalog"))
      .finally(() => setLoading(false));
  }, [heroId]);

  if (!heroId) return null;

  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose} data-testid="egg-hero-drawer">
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-2xl mx-auto my-4 rounded-2xl border-2 border-purple-500/40 bg-gradient-to-b from-[#1a0d2b] via-[#0a0a0a] to-[#1a0d2b] p-5 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="text-5xl">{data?.hero_icon || "🥚"}</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300">Fighter Catalog</div>
              <div className="text-2xl font-bold text-white">{data?.hero_name || "Loading..."}</div>
              {data?.canonized && (
                <div className="text-[11px] text-neutral-400">
                  {data.known_moves} of {data.total_moves} moves mastered · {data.mastery_pct}%
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="egg-hero-drawer-close"
            className="text-neutral-400 hover:text-white text-3xl leading-none"
          >×</button>
        </div>

        {loading && <div className="text-center py-8 text-neutral-500">Loading catalog...</div>}

        {!loading && data && !data.canonized && (
          <div className="text-center py-10 rounded-xl border border-dashed border-neutral-800 bg-black/40">
            <div className="text-5xl mb-2">🔒</div>
            <div className="text-sm text-neutral-400">{data.message}</div>
          </div>
        )}

        {!loading && data?.canonized && (
          <div className="space-y-4">
            {MOVE_TIERS_UI.map((tier) => {
              const meta = TIER_META[tier];
              const moves = data.tiers[tier] || [];
              return (
                <div key={tier} className={`rounded-xl border ${meta.border} ${meta.bg} p-3`} data-testid={`egg-tier-block-${tier}`}>
                  <div className={`text-[10px] uppercase tracking-[0.3em] ${meta.accent} font-bold mb-2 flex items-center gap-2`}>
                    <span className={`px-2 py-0.5 rounded ${meta.chip} text-[9px]`}>{meta.label}</span>
                    <span className="text-neutral-500">
                      {moves.filter((m) => m.hatched).length} / {moves.length} hatched
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {moves.map((m) => (
                      <div
                        key={m.move_id}
                        className={`rounded-lg p-2.5 border ${
                          m.hatched
                            ? "border-neutral-700 bg-black/40"
                            : "border-dashed border-neutral-800 bg-black/20"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {m.hatched ? (
                            <Sparkles size={14} className="text-emerald-400 mt-0.5" />
                          ) : (
                            <Lock size={14} className="text-neutral-500 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-white">{m.name}</div>
                            <div className="text-[11px] text-neutral-400 mt-0.5 leading-relaxed">{m.description}</div>
                            <div className={`mt-1.5 rounded px-2 py-0.5 text-[10px] font-mono ${
                              m.hatched
                                ? "bg-emerald-900/40 border border-emerald-500/40 text-emerald-200"
                                : "bg-black/60 border border-dashed border-neutral-700 text-neutral-500 italic"
                            }`} data-testid={`egg-move-input-${m.move_id}`}>
                              {m.hatched ? (
                                <div className="space-y-0.5">
                                  <div><span className="text-emerald-400">PC:</span> {m.input_pc || m.input}</div>
                                  {m.input_mobile && (
                                    <div><span className="text-neutral-400">📱 Mobile:</span> {m.input_mobile}</div>
                                  )}
                                </div>
                              ) : (
                                <div className="inline-flex items-center gap-1.5"><Lock size={10} /> Input locked — hatch to learn</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Main Component ────────────────────────────────────────
export default function DragoniousFightsEggNest() {
  const { user } = useAuth();
  const [state, setState]     = useState(null);
  const [heroes, setHeroes]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [hatching, setHatching] = useState(false);
  const [cracking, setCracking] = useState(false);
  const [reveal, setReveal]   = useState(null);
  const [openHero, setOpenHero] = useState(null);
  const [tick, setTick]       = useState(0);
  const [cheatWallOpen, setCheatWallOpen] = useState(false);

  const loadAll = async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/dragonious-egg/state`, { withCredentials: true }),
        axios.get(`${API}/dragonious-egg/heroes`, { withCredentials: true }),
      ]);
      setState(s.data);
      setHeroes(h.data);
    } catch (e) {
      toast.error("Could not load the Egg Nest");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) loadAll(); }, [user]);

  // Live cooldown ticker
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const cooldownLeft = useMemo(() => {
    if (!state?.last_hatch_at) return 0;
    const last = new Date(state.last_hatch_at).getTime();
    const now = Date.now();
    const elapsed = Math.floor((now - last) / 1000);
    return Math.max(0, (state.egg?.cooldown_hours || 12) * 3600 - elapsed);
  }, [state?.last_hatch_at, state?.egg?.cooldown_hours, tick]);

  const canHatch = cooldownLeft === 0;

  const doHatch = async () => {
    if (!canHatch || hatching) return;
    setHatching(true);
    setCracking(true);
    try {
      // Slight animation delay before firing the request
      await new Promise((r) => setTimeout(r, 900));
      const res = await axios.post(
        `${API}/dragonious-egg/hatch`,
        {},
        { withCredentials: true }
      );
      setReveal(res.data);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hatch failed");
    } finally {
      setHatching(false);
      setCracking(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center py-16 text-neutral-500" data-testid="egg-nest-signin-gate">
        Sign in to open the Egg Nest.
      </div>
    );
  }

  if (loading || !state) {
    return <div className="text-center py-16 text-neutral-500" data-testid="egg-nest-loading">Loading the Egg Nest...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="dragonious-egg-nest-root">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-purple-500/40 text-purple-300 mb-3">
          <Egg size={14} />
          <span className="font-accent text-xs tracking-[0.3em] uppercase">Dragonious Fighters · Egg Nest 🪹</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-white leading-[0.9] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          The <span className="text-red-400">Dragonious Fighters</span> Egg Nest
        </h2>
        <p className="text-neutral-400 max-w-2xl mx-auto text-sm">
          Every 12 hours, hatch one <span className="text-purple-300 font-bold">Dragonious Fighters Egg</span> to
          learn the <span className="text-emerald-300">exact command input</span> for one of your heroes&apos; moves.
          Your fighter has the move — but until you hatch its input from the Nest, you can&apos;t perform it.
          Duplicates auto-convert to currency — nothing is ever wasted.
        </p>
      </div>

      {/* Hero: Egg + Hatch */}
      <div className="grid md:grid-cols-2 gap-6 items-center rounded-2xl border-2 border-purple-500/40 bg-gradient-to-br from-[#1a0d2b] via-[#0a0a0a] to-[#1a0d2b] p-6 mb-6">
        <DragoniousEgg cracking={cracking} canHatch={canHatch} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-300 mb-1">The Egg</div>
          <div className="text-2xl font-bold text-white mb-2">Dragonious Fighters Egg</div>
          <div className="text-xs text-neutral-400 leading-relaxed mb-4">
            Purple &amp; black shell, sealed with a blood 🩸 sigil. Cracks open every 12 hours to reveal
            a random move from a random canonized fighter. Contents are hidden until the moment of hatching.
          </div>

          {/* Cooldown / Hatch */}
          <div className={`rounded-xl border-2 p-4 mb-3 ${canHatch ? "border-emerald-500/40 bg-emerald-900/20" : "border-purple-500/40 bg-purple-950/30"}`}>
            <div className="flex items-center gap-2 mb-1">
              <Timer size={14} className={canHatch ? "text-emerald-300" : "text-purple-300"} />
              <span className={`text-[10px] uppercase tracking-[0.3em] font-bold ${canHatch ? "text-emerald-300" : "text-purple-300"}`}>
                {canHatch ? "Ready to Hatch" : "Incubating"}
              </span>
            </div>
            <div className="text-2xl font-bold text-white font-mono" data-testid="egg-cooldown-timer">
              {formatCountdown(cooldownLeft)}
            </div>
          </div>

          <button
            onClick={doHatch}
            disabled={!canHatch || hatching}
            data-testid="egg-hatch-button"
            className={`w-full py-3 rounded-lg font-bold uppercase tracking-widest text-sm transition ${
              !canHatch || hatching
                ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                : "bg-gradient-to-r from-purple-600 to-red-600 text-white hover:brightness-125 shadow-lg shadow-purple-900/50"
            }`}
          >
            {hatching ? "Cracking..." : canHatch ? "🥚 Hatch the Egg 🩸" : "🔒 Locked"}
          </button>
        </div>
      </div>

      {/* Wallet + Progress */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="egg-wallet">
        {Object.entries(CURRENCY_META).map(([key, meta]) => (
          <div key={key} className="rounded-xl border border-neutral-800 bg-black/40 p-3 text-center" data-testid={`egg-wallet-${key}`}>
            <div className="text-2xl mb-1">{meta.icon}</div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">{meta.label}</div>
            <div className={`text-xl font-bold ${meta.color}`}>{state.wallet?.[key] || 0}</div>
          </div>
        ))}
      </div>

      {/* 🔒 Owner-only Cheat Codes Wall trigger (silent for non-owners) */}
      {state.is_owner && state.cheat_wall?.granted && (
        <div className="mb-6 rounded-xl border border-fuchsia-500/40 bg-gradient-to-r from-fuchsia-950/60 via-black/60 to-purple-950/60 p-3 flex items-center justify-between gap-3" data-testid="egg-cheat-wall-trigger-row">
          <div className="flex items-center gap-2 min-w-0">
            <KeyRound size={14} className="text-fuchsia-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold">Owner Debug</div>
              <div className="text-sm text-white truncate">Cheat Codes Wall unlocked · 4 hidden menus available</div>
            </div>
          </div>
          <button
            onClick={() => setCheatWallOpen(true)}
            data-testid="egg-cheat-wall-open"
            className="px-3 py-1.5 rounded-lg bg-fuchsia-700 hover:bg-fuchsia-600 text-white text-xs font-bold uppercase tracking-widest transition"
          >Open Wall</button>
        </div>
      )}

      <DragoniousCheatWall open={cheatWallOpen} onClose={() => setCheatWallOpen(false)} />

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-xl border border-neutral-800 bg-black/40 p-3 text-center">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Eggs Hatched</div>
          <div className="text-2xl font-bold text-white">{state.eggs_hatched_total}</div>
        </div>
        <div className="rounded-xl border border-neutral-800 bg-black/40 p-3 text-center">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Moves Known</div>
          <div className="text-2xl font-bold text-emerald-300">{state.total_hatched} / {state.total_moves}</div>
        </div>
        <div className="rounded-xl border border-neutral-800 bg-black/40 p-3 text-center">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Overall Mastery</div>
          <div className="text-2xl font-bold text-purple-300">{state.mastery_pct}%</div>
        </div>
      </div>

      {/* Roster mastery grid */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={12} className="text-yellow-400" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">Fighter Mastery</span>
          <span className="text-[10px] text-neutral-500">
            · {heroes?.canonized?.length || 0} canonized · {heroes?.pending?.length || 0} pending
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="egg-hero-grid">
          {heroes?.canonized?.map((h) => (
            <HeroMasteryCard key={h.hero_id} hero={h} onOpen={setOpenHero} />
          ))}
          {heroes?.pending?.map((h) => (
            <HeroMasteryCard key={h.hero_id} hero={h} onOpen={setOpenHero} />
          ))}
        </div>
      </div>

      {/* History */}
      {(state.history?.length || 0) > 0 && (
        <div className="mt-8 rounded-xl border border-neutral-800 bg-black/40 p-4" data-testid="egg-history">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 font-bold mb-2 flex items-center gap-2">
            <Coins size={12} /> Recent Hatches
          </div>
          <div className="space-y-1 text-xs">
            {[...state.history].reverse().slice(0, 8).map((h, i) => (
              <div key={i} className="flex items-center justify-between border-b border-neutral-900 pb-1">
                <div className="flex items-center gap-2">
                  <span>{h.duplicate ? "🔁" : "✨"}</span>
                  <span className="text-neutral-400">{h.hero_name}</span>
                  <span className="text-neutral-600">·</span>
                  <span className={TIER_META[h.tier]?.accent || "text-neutral-300"}>{h.move_name}</span>
                </div>
                {h.duplicate && h.reward && (
                  <span className="text-yellow-300 text-[10px]">
                    +{h.reward.amount} {CURRENCY_META[h.reward.currency]?.label}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <RevealModal result={reveal} onClose={() => setReveal(null)} />
      <HeroDetail heroId={openHero} onClose={() => setOpenHero(null)} />

      <style>{`
        @keyframes shake {
          0%,100% { transform: translate(0,0) rotate(0deg); }
          10% { transform: translate(-2px,-1px) rotate(-1deg); }
          20% { transform: translate(2px,1px) rotate(1deg); }
          30% { transform: translate(-2px,1px) rotate(-1deg); }
          40% { transform: translate(2px,-1px) rotate(1deg); }
          50% { transform: translate(-1px,-2px) rotate(-1deg); }
          60% { transform: translate(1px,2px) rotate(1deg); }
          70% { transform: translate(-1px,-1px) rotate(0deg); }
          80% { transform: translate(1px,1px) rotate(1deg); }
          90% { transform: translate(-1px,-1px) rotate(-1deg); }
        }
        .animate-shake { animation: shake 0.4s infinite; }
      `}</style>
    </div>
  );
}
